/**
 * Framework-free help-assistant turn runner (AB#26561 Task 8). Ported (behavior, not syntax)
 * from the React reference's `runTurn` / `handleRetry`
 * (syrenis-frontends/packages/syrenis-ui/.../HelpAssistant/HelpAssistant.tsx:518-637,715-720).
 *
 * Deliberate simplification vs. the React original (per the task-8 brief): `runTurn` takes a
 * raw `userText` string and pushes the user turn itself, rather than a pre-built `history`
 * array that already ends in the user turn. `retryTurn` (and, by the same mechanism, a
 * panel-side "regenerate") both reduce to the same primitive: drop a turn (and anything after
 * it), then re-run with the dropped turn's own user text.
 *
 * `state` is a plain object mutated in place - deliberately not a Vue construct, so this file
 * has no framework dependency. The host (help-assistant-panel.vue) can pass its own component
 * instance as `state`: Vue 2's reactivity is just property getters/setters on the instance, so
 * mutating `state.messages` via push/pop/splice and reassigning `state.streaming` etc. reacts
 * exactly as if the panel had done it itself. Only Array methods Vue 2 instruments for
 * reactivity (push, pop, splice) are used to mutate `state.messages` - never direct index
 * assignment, which Vue 2 cannot observe.
 *
 * `deps = { stream, api, context }`:
 * - `deps.stream` stands in for `streamHelpChat` (sse-client.js), pre-bound by the caller with
 *   whatever endpoint/headers/fetchImpl it needs - this file calls it with only the
 *   per-turn options (messages, handlers, signal, chatId, context).
 * - `deps.api` is a client shaped like `createHelpApiClient`'s return value (help-api-client.js);
 *   only `createChat` is used here.
 * - `deps.context` is the screen-context string threaded through to the stream request.
 *
 * See docs/work/AB26561-chat-docs-help-poc/chat-core-contract.md for the wire contract this
 * assumes.
 */
import { toSendWindow, toolActivityLabel } from './chat-core.js'

let uidCounter = 0

/**
 * Monotonic per-session id for a chat message, so the transcript's `v-for` can key on something
 * stable across content mutations (array index shifts under retry/regenerate, which drops and
 * re-runs turns - AB#26561 PR #2723 review). Exported so callers that assemble message objects
 * outside `runTurn` (e.g. the panel's chat-history load in `openChat`) can assign the same kind
 * of id.
 * @returns {number}
 */
export function nextUid () {
	return ++uidCounter
}

/**
 * @typedef {object} HelpTurnState
 * @property {{role: 'user'|'assistant', content: string, isError?: boolean, id?: number, timestamp?: string, uid?: number}[]} messages
 * @property {boolean} streaming
 * @property {string|null} toolActivity
 * @property {{actionId: string, toolName: string, summary: string, status: string}[]} pendingActions
 * @property {string|null} chatId
 * @property {AbortController|null} [abortController] Set for the lifetime of the in-flight
 *   stream so the caller (e.g. a Stop button, or cleanup on destroy) can cancel it; cleared once
 *   the turn settles.
 */

/**
 * @typedef {object} HelpTurnDeps
 * @property {(options: object) => Promise<void>} stream Pre-bound `streamHelpChat`; called with
 *   `{ messages, handlers, signal, chatId, context }`.
 * @property {{ createChat: (title?: string) => Promise<{chatId: string}> }} deps.api
 * @property {string|null} [context]
 */

/**
 * Runs one assistant turn: pushes `userText` as a new user message, ensures a server-side chat
 * exists (creating one titled with this turn's text on the first turn), then streams the answer
 * into a growing assistant message appended right after it. A no-op while a turn is already
 * streaming, or when `userText` is blank.
 * @param {{ state: HelpTurnState, deps: HelpTurnDeps, userText: string }} options
 * @returns {Promise<void>}
 */
export async function runTurn ({ state, deps, userText }) {
	if (state.streaming) return
	const trimmed = (userText ?? '').trim()
	if (!trimmed) return

	state.messages.push({ role: 'user', content: trimmed, timestamp: new Date().toISOString(), uid: nextUid() })
	state.messages.push({ role: 'assistant', content: '', uid: nextUid() })
	state.streaming = true
	state.toolActivity = null

	const controller = new AbortController()
	state.abortController = controller

	const appendToTail = updater => {
		const index = state.messages.length - 1
		state.messages.splice(index, 1, updater(state.messages[index]))
	}

	try {
		// New chat: mint the server-side chat row before streaming, titled with this turn's
		// user text (the server truncates it to 60 chars), so the chatId sent with the request
		// already exists. A creation failure degrades to a stateless turn rather than failing
		// the whole send - the user still gets an answer, just not a persisted one.
		if (!state.chatId) {
			try {
				const created = await deps.api.createChat(trimmed)
				state.chatId = created.chatId
			} catch {
				// continue without persistence
			}
		}

		await deps.stream({
			messages: toSendWindow(state.messages.slice(0, -1)),
			context: deps.context,
			chatId: state.chatId,
			signal: controller.signal,
			handlers: {
				onDelta: delta => {
					// A delta arriving is the clearest signal that whatever tool was running has
					// handed back to the model - clear the hint even if no "finished" event
					// landed for it (e.g. the last tool in a multi-tool turn).
					state.toolActivity = null
					appendToTail(tail => ({ ...tail, content: tail.content + delta }))
				},
				// Citations are not surfaced in this UI (product decision, matches the React
				// reference) - intentional no-op rather than dead state.
				onSources: () => {},
				// Spreads `tail` (rather than building a fresh object) so the placeholder's `uid`
				// survives the swap to an error bubble - otherwise the transcript's uid-keyed
				// v-for would see the error turn as a brand-new element instead of a mutation.
				onError: message => appendToTail(tail => ({ ...tail, content: message, isError: true })),
				// Only "started" needs to act - "finished" is a deliberate no-op so the hint
				// survives the gap between tools in a multi-tool turn; onDelta above and the
				// timestamp-stamping below are what actually clear it for good.
				onTool: activity => {
					if (activity.status === 'started') state.toolActivity = toolActivityLabel(activity.name)
				},
				onAction: action => {
					state.pendingActions.push({
						actionId: action.actionId,
						toolName: action.toolName,
						summary: action.summary,
						status: 'pending'
					})
				},
				onPersisted: ({ userMessageId, assistantMessageId }) => {
					const lastIndex = state.messages.length - 1
					if (lastIndex >= 0) {
						state.messages.splice(lastIndex, 1, { ...state.messages[lastIndex], id: assistantMessageId })
					}
					if (lastIndex - 1 >= 0) {
						state.messages.splice(lastIndex - 1, 1, { ...state.messages[lastIndex - 1], id: userMessageId })
					}
				}
			}
		})
	} finally {
		state.streaming = false
		state.toolActivity = null
		state.abortController = null

		// Drop the placeholder if the turn ended (most likely: aborted) before a single delta
		// arrived. The server rejects an empty message, so leaving it in place would 400 every
		// later send in this chat.
		const placeholderIndex = state.messages.length - 1
		const placeholder = state.messages[placeholderIndex]
		if (placeholder && placeholder.role === 'assistant' && placeholder.content === '' && !placeholder.isError) {
			state.messages.pop()
		}

		// Live-streamed assistant turns (success or error) are stamped client-side once the
		// stream completes - there's no server timestamp for a turn that hasn't been fetched
		// back yet. Guarded on the tail not already having one (e.g. a message replayed from
		// chat history keeps its own timestamp).
		const tailIndex = state.messages.length - 1
		const tail = state.messages[tailIndex]
		if (tail && !tail.timestamp) {
			state.messages.splice(tailIndex, 1, { ...tail, timestamp: new Date().toISOString() })
		}
	}
}

/**
 * Retries (or regenerates) the turn ending at `index`: drops that message and everything after
 * it - not just the failed pair itself - then re-runs `runTurn` with the dropped turn's own
 * user text. Used both for "Retry" on a specific error bubble (any error turn is retryable, not
 * just the most recent one - AB#26561 review amendment) and for "Regenerate" on the last
 * assistant answer, which is the same operation with `index` pinned to the last message.
 * A no-op if the message preceding `index` isn't a user turn, or while a turn is streaming.
 * @param {{ state: HelpTurnState, deps: HelpTurnDeps, index: number }} options
 * @returns {Promise<void>|undefined}
 */
export function retryTurn ({ state, deps, index }) {
	if (state.streaming) return
	const userMessage = state.messages[index - 1]
	if (!userMessage || userMessage.role !== 'user') return
	state.messages.splice(index - 1)
	return runTurn({ state, deps, userText: userMessage.content })
}
